import {
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Paper,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type CapacityDetailMember,
  useCapacityDetail,
} from "../../hooks/useKpi.js";

/**
 * „Kapazität im Detail" — macht die Soll-Linie des Stunden-Charts
 * nachvollziehbar: pro Mitglied × Monat die Stunden, die einfließen
 * (Ein-/Austritt, Stunden-Zeiträume, Abwesenheiten). Deaktivierte
 * Mitglieder erscheinen mit 0 + Badge statt lautlos zu fehlen; gepflegtes
 * Stunden-Soll aus den Zielen wird als Override-Zeile ausgewiesen.
 */

const MONTH_SHORT = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

const fmtH = (v: number) => Math.round(v).toLocaleString("de-DE");

/**
 * Der Ist-Wert einer Zelle. Ein Gedankenstrich statt einer 0, wenn die Person
 * im Spiegel gar nicht als Berater vorkommt: Eine harte Null liest sich wie
 * Nichtstun, obwohl es eine Namensluecke ist (`consultant_name` ist Freitext
 * aus Salesforce).
 */
const istText = (h: number, imSpiegelBekannt: boolean) =>
  imSpiegelBekannt ? fmtH(h) : "\u2013";

/**
 * Beschriftung des Abzeichens am Namen. Beide Quellen werden nur dann
 * getrennt genannt, wenn es auch beide gibt — sonst stuende dort eine Null,
 * die nichts erklaert.
 */
function abzeichenText(
  t: TFunction,
  z: { segment: number; fremdbereich: number; gesamt: number },
): string {
  // Gerundet vergleichen, nicht roh. Eine Viertelstunde ist groesser als null,
  // wird aber als „0" angezeigt — die erste Fassung schrieb deshalb
  // „(27 Projektumsetzung, 0 anderer Bereich)". Eine Null in einer
  // Aufschluesselung erklaert nichts, sie stellt nur eine Frage.
  if (Math.round(z.segment) > 0 && Math.round(z.fremdbereich) > 0) {
    return t("pages.kpi.capacity.fremdBadgeBeides", {
      stunden: fmtH(z.gesamt),
      fremdbereich: fmtH(z.fremdbereich),
      segment: fmtH(z.segment),
    });
  }
  if (Math.round(z.fremdbereich) >= Math.round(z.segment)) {
    return t("pages.kpi.capacity.fremdBadgeBereich", {
      stunden: fmtH(z.fremdbereich),
    });
  }
  return t("pages.kpi.capacity.fremdBadgeSegment", {
    stunden: fmtH(z.segment),
  });
}

const jahr = (werte: number[]) => werte.reduce((a, b) => a + b, 0);

/**
 * Die Zusatzstunden einer Person — zwei Quellen, eine Zahl.
 *
 * `segment` sind Stunden auf Projekten eines anderen KUNDENSEGMENTS,
 * `fremdbereich` solche auf Projekten außerhalb des Servicegeschäfts
 * (Salesforce-Bereich „Projektumsetzung" u. a.). Beide schaltet derselbe Haken
 * zu: Der Betrachter trifft eine Entscheidung, nicht vier. Wo die Stunden
 * herkommen, sagt das Abzeichen am Namen.
 */
const zusatzJahr = (m: CapacityDetailMember) => {
  const segment = jahr(zaehlbar(m, m.billedElsewhere));
  const fremdbereich = jahr(zaehlbar(m, m.billedNonService));
  return { segment, fremdbereich, gesamt: segment + fremdbereich };
};

/**
 * Nur die Monate, die in dieser Zeile auch angezeigt werden.
 *
 * Eine Zelle ohne Kapazität zeigt „–" und verschweigt ihre Stunden — die
 * Person gehörte in dem Monat nicht zum Team. Die Jahressumme summierte
 * trotzdem alle zwölf Monate, und das Abzeichen ebenso. Bei einem Kollegen,
 * der im Februar ausgeschieden ist, stand am Namen „+509 h", die Zeile zeigte
 * aber nur zwei Monate mit zusammen 26 — eine Zahl, die in keiner Zelle
 * vorkommt und nirgends nachzurechnen ist (aufgefallen auf Staging,
 * 2026-08-20). Was die Zeile nicht zeigt, zählt sie auch nicht.
 */
const zaehlbar = (m: CapacityDetailMember, werte: number[]) =>
  werte.map((v, i) => ((m.monthly[i] ?? 0) > 0 ? v : 0));

const zusatzMonat = (
  m: CapacityDetailMember,
  monat: number,
  mitFremden: boolean,
) => {
  if (!mitFremden) return { segment: 0, fremdbereich: 0, gesamt: 0 };
  const segment = m.billedElsewhere[monat] ?? 0;
  const fremdbereich = m.billedNonService[monat] ?? 0;
  return { segment, fremdbereich, gesamt: segment + fremdbereich };
};

const summe = (m: CapacityDetailMember, mitFremden: boolean) =>
  jahr(zaehlbar(m, m.billed)) + (mitFremden ? zusatzJahr(m).gesamt : 0);

const istGesamt = (
  members: CapacityDetailMember[],
  monat: number,
  mitFremden: boolean,
) =>
  members.reduce(
    (a, m) =>
      // Auch die Summenzeile zaehlt nur, was in der Spalte steht.
      (m.monthly[monat] ?? 0) > 0
        ? a + (m.billed[monat] ?? 0) + zusatzMonat(m, monat, mitFremden).gesamt
        : a,
    0,
  );

export function CapacityDetailPanel({
  segmentId,
  year,
  mitFremden,
}: {
  segmentId: string;
  year: number;
  /**
   * Stunden mitzaehlen, die Mitglieder in ANDEREN Bereichen gebucht haben.
   * Geteilter Zustand mit dem Stunden-Diagramm — er steckt in der URL, sonst
   * behauptete derselbe Schalter in zwei Reitern zwei verschiedene Dinge.
   */
  mitFremden: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useCapacityDetail(segmentId, year, open);

  const hasOverride = (data?.overrides ?? []).some((o) => o != null);
  // Mitglieder, deren Name in keiner Projektbesetzung vorkommt — ihre Zeile
  // zeigt einen Gedankenstrich statt 0, und der Hinweis nennt sie beim Namen.
  const ohneSpiegel = (data?.members ?? [])
    .filter((m) => !m.imSpiegelBekannt)
    .map((m) => m.fullName);

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between">
        <Text fw={600} size="sm">
          {t("pages.kpi.capacity.title")}
        </Text>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => setOpen((o) => !o)}
        >
          {open
            ? t("pages.kpi.capacity.hide")
            : t("pages.kpi.capacity.show")}
        </Button>
      </Group>

      <Collapse in={open}>
        {isLoading || !data ? (
          <Skeleton height={160} mt="sm" />
        ) : (
          <>
            <Text size="xs" c="dimmed" mt={4} mb="sm">
              {t("pages.kpi.capacity.hint")}
            </Text>
            {ohneSpiegel.length > 0 && (
              <Alert color="orange" mb="sm" p="xs">
                <Text size="xs">
                  {t("pages.kpi.capacity.ohneSpiegelHinweis", {
                    namen: ohneSpiegel.join(", "),
                  })}
                </Text>
              </Alert>
            )}
            {hasOverride && (
              <Alert color="yellow" mb="sm" p="xs">
                <Text size="xs">
                  {t("pages.kpi.capacity.overrideWarning")}
                </Text>
              </Alert>
            )}
            <Table.ScrollContainer minWidth={900}>
              <Table withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("pages.kpi.capacity.colMember")}</Table.Th>
                    {MONTH_SHORT.map((m) => (
                      <Table.Th key={m} ta="right">
                        {m}
                      </Table.Th>
                    ))}
                    <Table.Th ta="right">
                      {t("pages.kpi.capacity.colTotal")}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.members.map((m) => (
                    <Table.Tr key={m.employeeId}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="xs" fw={500}>
                            {m.fullName}
                          </Text>
                          {!m.active && (
                            <Badge
                              size="xs"
                              color="gray"
                              title={t("pages.kpi.capacity.inactiveHint")}
                            >
                              {t("pages.kpi.capacity.inactiveBadge")}
                            </Badge>
                          )}
                          {/*
                            Die Jahressumme am Namen, weil die Monate mit
                            fremden Stunden nicht die sind, die man gerade auf
                            dem Schirm hat: Wer im Maerz woanders war, faellt in
                            der August-Spalte sonst nicht auf. Nur bei gesetztem
                            Haken — sonst staende hier eine Zahl, die in keiner
                            Zelle der Zeile vorkommt.
                          */}
                          {mitFremden && zusatzJahr(m).gesamt > 0 && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color="orange"
                              title={t("pages.kpi.capacity.fremdBadgeHint")}
                            >
                              {abzeichenText(t, zusatzJahr(m))}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      {m.monthly.map((kapazitaet, i) => {
                        // Der fremde Anteil wird BEZIFFERT, nicht still
                        // eingerechnet. Sonst aendert der Schalter oben Zahlen,
                        // ohne zu sagen welche — und beim Scrollen ist nicht
                        // mehr erkennbar, wessen Zeile gewachsen ist.
                        const zusatzWerte = zusatzMonat(m, i, mitFremden);
                        const ist = (m.billed[i] ?? 0) + zusatzWerte.gesamt;
                        // Der Gedankenstrich gilt nur der Namensluecke im
                        // Projektspiegel. Die Stunden ausserhalb des
                        // Servicegeschaefts haengen an der Salesforce-Kennung
                        // und sind auch dann belastbar — sie werden deshalb
                        // beziffert statt mitverschluckt.
                        const zusatz =
                          zusatzWerte.gesamt > 0 &&
                          (m.imSpiegelBekannt || zusatzWerte.fremdbereich > 0)
                            ? ` (+${fmtH(zusatzWerte.gesamt)})`
                            : "";
                        return (
                          <Table.Td key={i} ta="right">
                            <Text
                              size="xs"
                              c={kapazitaet === 0 ? "dimmed" : undefined}
                            >
                              {kapazitaet === 0
                                ? "–"
                                : `${istText(ist, m.imSpiegelBekannt)}${zusatz} / ${fmtH(kapazitaet)}`}
                            </Text>
                          </Table.Td>
                        );
                      })}
                      <Table.Td ta="right">
                        <Text size="xs" fw={500}>
                          {istText(summe(m, mitFremden), m.imSpiegelBekannt)} /{" "}
                          {fmtH(m.total)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td>
                      <Text size="xs" fw={700}>
                        {t("pages.kpi.capacity.sumRow")}
                      </Text>
                    </Table.Td>
                    {data.totals.map((h, i) => (
                      <Table.Td key={i} ta="right">
                        <Text size="xs" fw={700}>
                          {fmtH(istGesamt(data.members, i, mitFremden))} /{" "}
                          {fmtH(h)}
                        </Text>
                      </Table.Td>
                    ))}
                    <Table.Td ta="right">
                      <Text size="xs" fw={700}>
                        {fmtH(
                          data.members.reduce(
                            (a, m) => a + summe(m, mitFremden),
                            0,
                          ),
                        )}{" "}
                        / {fmtH(data.totals.reduce((a, b) => a + b, 0))}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  {hasOverride && (
                    <Table.Tr>
                      <Table.Td>
                        <Text size="xs" fw={600} c="yellow.8">
                          {t("pages.kpi.capacity.overrideRow")}
                        </Text>
                      </Table.Td>
                      {data.overrides.map((o, i) => (
                        <Table.Td key={i} ta="right">
                          <Text size="xs" c="yellow.8">
                            {o == null ? "–" : fmtH(o)}
                          </Text>
                        </Table.Td>
                      ))}
                      <Table.Td />
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}
      </Collapse>
    </Paper>
  );
}
